import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { Plus, Trash2 } from "lucide-react"
import { useReveniIntegrationQuery } from "modules/settings/hooks/reveni/useReveniIntegrationQuery"
import { useUpsertReveniIntegrationMutation } from "modules/settings/hooks/reveni/useUpsertReveniIntegrationMutation"
import { useEffect } from "react"
import { useFieldArray, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpsertReveniIntegrationSchema } from "schema"
import { z } from "zod"

export const ReveniAuthModal = () => {
  const { t } = useTranslation()
  const form = useForm<z.infer<typeof UpsertReveniIntegrationSchema>>({
    resolver: zodResolver(UpsertReveniIntegrationSchema),
    defaultValues: {
      stores: [{ name: "", apiKey: "" }],
    },
    mode: "onChange",
  })
  const { append, fields, remove } = useFieldArray({
    control: form.control,
    name: "stores",
  })
  const navigate = useNavigate()
  const { mutateAsync } = useUpsertReveniIntegrationMutation()
  const { data } = useReveniIntegrationQuery()

  const onSubmit = async (data: z.infer<typeof UpsertReveniIntegrationSchema>) => {
    await mutateAsync(data)
    onOpenChange()
  }

  const onOpenChange = (isOpen: boolean = false) => {
    if (!isOpen) {
      navigate({ to: "/settings/integrations/reveni" })
    }
  }

  useEffect(() => {
    if (data) {
      form.reset({
        stores: data.stores.length > 0 ? data.stores : [{ name: "", apiKey: "" }],
      })
    }
  }, [data, form])

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("settings.integrations.reveni.authModal.title")}</DialogTitle>
          <DialogDescription>
            {t("settings.integrations.reveni.authModal.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            errorTranslations={getErrorTranslations()}
          >
            <div className="flex flex-col w-full gap-4">
              {fields.map((store, index) => (
                <div key={store.id} className="flex flex-col gap-4 rounded-md border p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">
                      {t("settings.integrations.reveni.authModal.form.store", {
                        number: index + 1,
                      })}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={fields.length === 1}
                      onClick={() => remove(index)}
                      aria-label={t("settings.integrations.reveni.authModal.form.removeStore")}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                  <FormField
                    control={form.control}
                    name={`stores.${index}.name`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("settings.integrations.reveni.authModal.form.storeName")}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} required />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`stores.${index}.apiKey`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("settings.integrations.reveni.authModal.form.apiKey")}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} required />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => append({ name: "", apiKey: "" })}
              >
                <Plus className="size-4" />
                {t("settings.integrations.reveni.authModal.form.addStore")}
              </Button>
            </div>
            <DialogFooter>
              <Button disabled={form.formState.isLoading || !form.formState.isValid}>
                {t("settings.integrations.reveni.authModal.form.submit")}
              </Button>
            </DialogFooter>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
